import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Container } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import InfoIcon from '@mui/icons-material/Info'

import { Caption, Text1 } from 'ui/shorthands/Texts'
import { consultationMethodNames } from 'types/applicant'

import { StackItem } from 'ui/layout/StackItem'
import { Layout } from 'ui/layout/Layout'
import { AppBarPatient } from 'ui/partials/AppBar'

import { ThumbnailList } from 'ui/utils/Thumbnails'
import { jstNewDate } from 'ui/utils/jstDatetime'

import nl2br from 'react-nl2br'

import type {
  MedicalInstitutionProps,
  ReservationFormProps,
} from 'types/applicant'

import { toDateDayTimeStringFromDateTimeString } from 'ui/utils/AvailableDateTimes'
import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'
import BillingSection from 'ui/pages/med/Reservations/ReservationDetail/BillingSection'
import { PersonalSettlementsNames } from 'types/applicant'
import { BillingStatusLabels, UpdatedByLabels } from 'types/common'
import type { BankTransfer, Billing, PrescriptionSetting } from 'types/med'
import type { CallMethodNames } from 'types/common'
import { VideoAppInfo } from 'ui/partials/VideoAppDescription'
import { reformatBirthday } from 'ui/utils/dateFormatUtils'
import { parseDatetimeSecondString } from 'ui/utils/dateFormatUtils'
import { subMinutes } from 'date-fns'
import { reservationStatusLabelText } from 'ui/partials/reservationStatusLabelText'
import PharmacyInfo from 'ui/partials/PharmacyInfo'
import theme from 'ui/theme'
import {
  howToReceiveMedicineValues,
  TayorisContent,
} from './Reservation/Reservation'
import { hasInfectedFiles } from 'ui/utils/imageUtils'
import { DepartmentSettingsProps } from 'types/med'
import LinkUrlText from 'ui/utils/LinkUrlText'
import TodokusuriText, {
  howToReceiveMedicineDirectValue,
} from 'ui/partials/TodokusuriText'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
  },

  informationWrapper: {
    backgroundColor: theme.palette.background.paper,
  },

  informationHeader: {
    marginTop: 0,
    paddingTop: theme.spacing(2.5),
  },

  informationHeaderIcon: {
    fontSize: 32,
    marginRight: theme.spacing(2),
  },

  informationContent: {
    padding: theme.spacing(1, 0, 4),
  },

  nameContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    justifyContent: 'center',
  },

  kana: {
    fontSize: 12,
    lineHeight: 1,
  },

  category: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(2),
  },

  vcDescriptionWrapper: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  },

  vcStartButtonWrapper: {
    flexShrink: 0,
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
  },

  videoAppInfo: {
    paddingTop: theme.spacing(3),
  },

  preWrap: {
    whiteSpace: 'pre-wrap',
  },

  controlWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },

  text: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
  },

  attention: {
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.secondary.light,
    fontWeight: 'bold',
  },

  infectedText: {
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
  },
}))

export type ReservationReadProps = {
  /**
   * 医療機関データ
   */
  med: MedicalInstitutionProps
  /**
   * セットする既存値
   */
  reservation: ReservationFormProps
  /**
   * 科目設定
   */
  settings?: DepartmentSettingsProps
  /**
   *キャンセルボタンを押した時
   */
  onSubmitCancel: () => Promise<void>
  /**
   * 編集ボタンを押した時
   */
  onclickEditHandler: () => void
  /**
   * ビデオ通話開始時に発火
   */
  onClickVideoChatStart?: (app: CallMethodNames) => Promise<void>

  /**
   * 請求情報
   */
  billing?: {
    billings?: Billing[]
    /** 銀行決済情報 (reservation.settlement_method === 'bank_transfer'の場合に指定) */
    bankTransfer?: BankTransfer
  }

  /**
   * 処方の設定
   */
  prescriptionSetting?: PrescriptionSetting
}

const VirusScanResultInfectedText: React.FC = () => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  return (
    <span className={classes.infectedText}>
      {t(
        '添付されたファイルは、問題があるため表示できません。ファイルのウィルスチェックを実施して、問題がないか確認してください。'
      )}
    </span>
  )
}

const ReservationRead: React.FC<ReservationReadProps> = ({
  med,
  reservation,
  settings,
  onSubmitCancel,
  onclickEditHandler,
  onClickVideoChatStart,
  billing,
  prescriptionSetting,
}: ReservationReadProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const isCanceled = reservation.status === 'canceled'
  const showSettlementMethod = !(isCanceled || !reservation.settlement_method)
  const vcDisabled = reservation.status !== 'before'

  const [showDetail, setShowDetail] = useState<boolean>(false)
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false)

  const toggleCancelConfirm = () => {
    setShowCancelConfirm(!showCancelConfirm)
  }

  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const detailContent = (
    <>
      <StackItem label={t('予約ID')}>
        <Text1>{reservation?.reservation_number}</Text1>
      </StackItem>
      <StackItem label={t('予約作成日時')}>
        <Text1>
          {reservation?.created_at &&
            `${toDateDayTimeStringFromDateTimeString(
              reservation.created_at,
              true
            )}（${t('日本時間')}）`}
        </Text1>
      </StackItem>
      <StackItem label={t('最終更新日時')}>
        <Text1>
          {reservation?.updated_at &&
            `${toDateDayTimeStringFromDateTimeString(
              reservation.updated_at,
              true
            )}（${t('日本時間')}）`}
          {reservation?.updated_by && UpdatedByLabels[reservation.updated_by]
            ? t(UpdatedByLabels[reservation.updated_by])
            : null}
        </Text1>
      </StackItem>
      <StackItem label={t('通知')} alignTop>
        <Text1>
          <Trans
            i18nKey={
              'trans.予約前日と予約前にメールでリマインドします。<br />@yadoc.jpからのメールが受信できるように設定をお願いします。'
            }
          />
        </Text1>
      </StackItem>
    </>
  )

  const apiErrorModal = (
    <Modal
      open={!!errorMessage}
      handleClose={() => {
        setErrorMessage(undefined)
      }}
    >
      <ConfirmContent
        onSubmit={() => {
          setErrorMessage(undefined)
        }}
        title={errorMessage}
      />
    </Modal>
  )

  const [vcButtonDisabled, setVcButtonDisabled] = useState(true)
  const [isEditable, setIsEditable] = useState(reservation.status === 'before')

  const [callMethodIsVc, setCallMethodIsVc] = useState(false)

  useEffect(() => {
    setCallMethodIsVc(reservation?.call_method?.startsWith?.('video_') || false)

    const { reservation_datetime_start } = reservation
    if (!reservation_datetime_start) return
    const startDate = parseDatetimeSecondString(reservation_datetime_start)
    const msToCallable =
      subMinutes(startDate, 15).valueOf() - jstNewDate.valueOf()

    // NOTE: 通話可能になるまで待つ
    if (0 < msToCallable) {
      const maxMs = 10 * 86400 * 1000 // NOTE: setTimeoutの最大値（約25日）より小さい値
      if (maxMs < msToCallable) {
        setVcButtonDisabled(true)
        return
      }
      const timerId = setTimeout(() => setVcButtonDisabled(false), msToCallable)
      return () => clearTimeout(timerId) // NOTE: reservationが変わった際に走る
    } else {
      setVcButtonDisabled(false)
    }

    const msToReservationStartTIme = startDate.valueOf() - jstNewDate.valueOf()

    // NOTE: 編集不可になるまで待つ
    if (0 < msToReservationStartTIme) {
      const maxMs = 10 * 86400 * 1000 // NOTE: setTimeoutの最大値（約25日）より小さい値
      if (maxMs < msToCallable) {
        setIsEditable(true)
        return
      }
      const timerId = setTimeout(
        () => setIsEditable(false),
        msToReservationStartTIme
      )
      return () => clearTimeout(timerId) // NOTE: reservationが変わった際に走る
    } else {
      setIsEditable(false)
    }
  }, [reservation])

  const onSubmitCancelHandler = () => {
    onSubmitCancel().catch(() => {
      setErrorMessage(t('errors.予約の取消に失敗しました'))
    })
  }

  const howToReceiveMedicineLabel = howToReceiveMedicineValues.find(
    ({ value }) => value === reservation.how_to_receive_medicine
  )?.label

  const isTayoris =
    reservation?.pharmacy_service === 'tayoris_health_management_support'
  const isTodokusuri = reservation?.pharmacy_service === 'todokusuri'

  const tayoRisContent = (
    <div className={classes.category}>
      <StackItem label={t('薬局選択')} alignTop>
        <TayorisContent />
      </StackItem>
    </div>
  )

  const pharmacySelectContent = (
    <>
      {reservation.pharmacy && (
        <div className={classes.category}>
          <StackItem
            label={t('薬局選択')}
            alignTop={theme.spacing(2.5)}
            childNoPadding
          >
            <PharmacyInfo
              showDetail
              pharmacy={reservation.pharmacy}
              type={'personal'}
            />
          </StackItem>
        </div>
      )}
      {isTodokusuri ? (
        <div className={classes.category}>
          <StackItem label={t('薬局選択')} alignTop>
            <div>
              <Text1>
                <TodokusuriText />
              </Text1>
            </div>
            <Caption>
              {t(
                'とどくすりを利用するには会員登録（無料）が必要です。また電話番号情報を、医療機関からとどくすりに共有させていただきます。ご了承ください。'
              )}
            </Caption>
          </StackItem>
        </div>
      ) : reservation?.prescription_medicine === 'direct' ? (
        <div className={classes.category}>
          <StackItem label={t('薬局選択')}>
            <Text1>{howToReceiveMedicineDirectValue}</Text1>
          </StackItem>
        </div>
      ) : null}
    </>
  )

  return (
    <Layout header={<AppBarPatient>{med.name} 予約</AppBarPatient>} showFooter>
      {settings?.information_for_reservation && (
        <div className={classes.informationWrapper}>
          <Container maxWidth="md">
            <h3 className={classes.informationHeader}>
              <Box display={'flex'} alignItems={'center'}>
                <InfoIcon
                  color={'primary'}
                  className={classes.informationHeaderIcon}
                />
                {t('予約された方へのご連絡')}
              </Box>
            </h3>
            <div className={classes.informationContent}>
              <Text1>
                <LinkUrlText>
                  {settings.information_for_reservation}
                </LinkUrlText>
              </Text1>
            </div>
          </Container>
        </div>
      )}
      <Container maxWidth="md">
        <div className={classes.category}>
          <div className={cx(isCanceled && classes.attention)}>
            <StackItem
              label={
                <span className={cx(isCanceled && classes.attention)}>
                  {t('ステータス')}
                </span>
              }
            >
              <Text1 className={cx(isCanceled && classes.attention)}>
                {reservationStatusLabelText(reservation)}
              </Text1>
            </StackItem>
          </div>
          <StackItem label={t('受付科目')}>
            <Text1>{reservation?.reservation_department_name}</Text1>
          </StackItem>
          <StackItem label={t('予約日時')}>
            <Text1>
              {reservation?.reservation_datetime_start &&
                toDateDayTimeStringFromDateTimeString(
                  reservation?.reservation_datetime_start
                )}
              〜（{t('日本時間')}）
            </Text1>
          </StackItem>
          {reservation?.free_description && (
            <StackItem label={t('自由記述欄')} alignTop>
              <Text1>
                {reservation?.free_description &&
                  nl2br(reservation.free_description)}
              </Text1>
            </StackItem>
          )}

          {reservation?.attachment_images?.length ? (
            <StackItem
              label={t('ファイル添付')}
              borderTop
              alignTop
              description={
                hasInfectedFiles(reservation.attachment_images) ? (
                  <VirusScanResultInfectedText />
                ) : undefined
              }
            >
              <ThumbnailList images={reservation.attachment_images} />
            </StackItem>
          ) : null}
          {settings?.use_in_person_consultation &&
            reservation?.consultation_method && (
              <StackItem label={t('受診方法')} alignTop>
                <Text1>
                  {consultationMethodNames[reservation.consultation_method]}
                </Text1>
              </StackItem>
            )}
          {reservation?.call_method &&
          reservation?.consultation_method !== 'in_person' ? (
            <StackItem label={t('通話方法')} alignTop>
              {reservation?.call_method === 'phone' && (
                <Text1>
                  {reservation?.call_method &&
                    reservation?.call_method_label &&
                    t(reservation.call_method_label)}
                  <br />
                  {t(
                    '予約時刻が近づきましたら、施設から電話がかかります。準備してお待ちください。'
                  )}
                </Text1>
              )}
              {callMethodIsVc && (
                <div className={classes.vcDescriptionWrapper}>
                  <Text1>
                    {reservation?.call_method &&
                      reservation?.call_method_label &&
                      t(reservation.call_method_label)}
                    <br />
                    {t(
                      '予約日時になりましたら、右のボタンからビデオ通話を開いてお待ちください。予約日時の15分前からビデオ通話が開けるようになります。'
                    )}
                  </Text1>
                  {apiErrorModal}
                  <div className={classes.vcStartButtonWrapper}>
                    <Button
                      onClick={() => {
                        reservation.call_method &&
                          onClickVideoChatStart &&
                          onClickVideoChatStart(reservation.call_method).catch(
                            () => {
                              setErrorMessage(
                                t('errors.ビデオ通話を開始できません')
                              )
                            }
                          )
                      }}
                      variant={'contained'}
                      color={'primary'}
                      disabled={vcButtonDisabled || vcDisabled}
                    >
                      {t('ビデオ通話')}
                    </Button>
                  </div>
                </div>
              )}
              {reservation.call_method && callMethodIsVc && (
                <div className={classes.videoAppInfo}>
                  <VideoAppInfo app={reservation.call_method} />
                </div>
              )}
            </StackItem>
          ) : (
            <StackItem label={t('連絡事項')} alignTop>
              <div className={classes.preWrap}>
                {reservation?.reservation_message}
              </div>
            </StackItem>
          )}
          {reservation.how_to_receive_medicine && (
            <StackItem label={t('薬の受け取り方法')}>
              <Text1>
                {howToReceiveMedicineLabel
                  ? t(howToReceiveMedicineLabel)
                  : null}
              </Text1>
            </StackItem>
          )}
          <StackItem
            label={t('詳細')}
            toggleContent={detailContent}
            onClickToggle={setShowDetail}
          >
            <Text1>
              {!showDetail && t('予約ID・予約作成日時・予約更新日時・通知')}
            </Text1>
          </StackItem>
        </div>
        <div className={classes.category}>
          <StackItem label={t('氏名')}>
            <Box className={classes.nameContainer}>
              {reservation?.applicant_last_name_kana && (
                <Text1 className={classes.kana}>
                  {reservation.applicant_last_name_kana}{' '}
                  {reservation?.applicant_first_name_kana}
                </Text1>
              )}
              <Text1>
                {reservation?.applicant_last_name}{' '}
                {reservation?.applicant_first_name}
              </Text1>
            </Box>
          </StackItem>
          <StackItem label={t('生年月日')}>
            <Text1>
              {reservation?.applicant_birthday &&
                reformatBirthday(reservation?.applicant_birthday)}
            </Text1>
          </StackItem>
          <StackItem label={t('電話番号')}>
            <Text1>{reservation?.applicant_phone}</Text1>
          </StackItem>
          {reservation?.applicant_email && (
            <StackItem label={t('メールアドレス')}>
              <Text1>{reservation?.applicant_email}</Text1>
            </StackItem>
          )}
          {(reservation?.applicant_address ||
            reservation?.applicant_postal_code) && (
            <StackItem label={t('住所')}>
              <Text1>
                {reservation?.applicant_postal_code}{' '}
                {reservation?.applicant_address}
              </Text1>
            </StackItem>
          )}
          {reservation?.registration_card_number && (
            <StackItem label={t('診察券番号')}>
              <Text1>{reservation.registration_card_number}</Text1>
            </StackItem>
          )}
          {!!reservation.online_eligibility_verification_agreed && (
            <StackItem label={t('マイナ保険証')} borderTop alignTop>
              <Text1>{t('同意済み')}</Text1>
            </StackItem>
          )}
          {!reservation.online_eligibility_verification_agreed &&
          reservation?.insurance_card_images?.length ? (
            <StackItem
              label={t('保険証')}
              borderTop
              alignTop
              description={
                hasInfectedFiles(reservation.insurance_card_images) ? (
                  <VirusScanResultInfectedText />
                ) : undefined
              }
            >
              <ThumbnailList images={reservation.insurance_card_images} />
            </StackItem>
          ) : null}
          {reservation?.certificate_images?.length ? (
            <StackItem
              label={t('保険証以外の証書')}
              borderTop
              alignTop
              description={
                hasInfectedFiles(reservation.certificate_images) ? (
                  <VirusScanResultInfectedText />
                ) : undefined
              }
            >
              <ThumbnailList images={reservation.certificate_images} />
            </StackItem>
          ) : null}
          {reservation?.identity_verification_images?.length ? (
            <StackItem
              label={t('本人確認書類')}
              borderTop
              alignTop
              description={
                hasInfectedFiles(reservation.identity_verification_images) ? (
                  <VirusScanResultInfectedText />
                ) : undefined
              }
            >
              <ThumbnailList
                images={reservation.identity_verification_images}
              />
            </StackItem>
          ) : null}
        </div>
        <div className={classes.category}>
          {!billing?.billings?.length ? (
            <>
              {showSettlementMethod && (
                <StackItem label={t('決済方法')}>
                  <Text1>
                    {reservation?.settlement_method &&
                      t(
                        PersonalSettlementsNames[reservation.settlement_method]
                      )}
                    {reservation?.settlement_method !== 'next_visit' &&
                      reservation.settlement_status &&
                      BillingStatusLabels[reservation.settlement_status] &&
                      `: ${t(
                        BillingStatusLabels[reservation.settlement_status]
                      )}`}
                  </Text1>
                </StackItem>
              )}
            </>
          ) : (
            <BillingSection
              reservation={reservation}
              billings={billing.billings}
              bankTransfer={billing.bankTransfer}
              type={'personal'}
            />
          )}
        </div>
        {
          // NOTE: タヨリスはクエリパラメータにより自動で設定されるため配送の設定値では最優先になっている
          isTayoris ? tayoRisContent : pharmacySelectContent
        }
        {isEditable && (
          <div className={classes.controlWrapper}>
            <Button color="secondary" onClick={toggleCancelConfirm}>
              {t('予約の取消')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onclickEditHandler}
            >
              {t('編集')}
            </Button>
          </div>
        )}
        <Modal open={showCancelConfirm} handleClose={toggleCancelConfirm}>
          <ConfirmContent
            onCancel={toggleCancelConfirm}
            onSubmit={onSubmitCancelHandler}
            title={t('予約を取り消しますか？')}
            submitLabel={t('取り消す')}
          />
        </Modal>
      </Container>
    </Layout>
  )
}

export default ReservationRead
